import { ActionDispatching, AdminInfoData } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { safeNumericString } from 'app/infrastructure/data/sanitize';
import { CodeSelectorState, ViewMode } from './types';
import * as actions from './action-types';
import { api } from './api';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { STATE } from 'app/constants';
import { CodeSelectorTranslation, codeSelectorTranslations as text, viewModeCacheKeyCaseTypes } from './constants';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { clientCache } from 'app/infrastructure/universal';
import { ChangeViewModeCaseTypes, RequestLoadDraftsCount, ReceiveLoadDraftsCount, FailLoadDraftsCount } from './action-types';

export const actionCreators = {
  loadCaseTypes: (): ActionDispatching<any, CodeSelectorState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestCodes());

      const caseTypes = await api.caseTypes();
      dispatch(new actions.ReceiveCodes(caseTypes));
    }),

  loadConnectedCaseTypes: (
    draftIds: string | string[],
    connectionTypeIds: string | string[]
  ): ActionDispatching<any, CodeSelectorState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestCodes());
      const draftId = Array.isArray(draftIds) ? draftIds.slice(-1)[0] : draftIds;
      const connectionTypeId = Array.isArray(connectionTypeIds) ? connectionTypeIds.slice(-1)[0] : connectionTypeIds;
      const caseTypes = await api.connectedCaseTypes(safeNumericString(draftId), safeNumericString(connectionTypeId));
      dispatch(new actions.ReceiveCodes(caseTypes));
    }),

  loadDashboards: (): ActionDispatching<any, CodeSelectorState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestCodes());

      const dashboards = await api.dashboards();
      dispatch(new actions.ReceiveCodes(dashboards.rootCodes));
    }),

  loadCompanyReports: (): ActionDispatching<any, CodeSelectorState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestCodes());

      const reports = await api.companyReports();
      dispatch(new actions.ReceiveCodes(reports.rootCodes));
    }),

  loadCodetableAdministrationList: (): ActionDispatching<any, CodeSelectorState> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestCodes());

      try {
        const codetables = await api.codetableAdministrationList();
        codetables.rootCodes && dispatch(new actions.ReceiveCodes(codetables.rootCodes));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.codeSelector,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && err.userMessages) {
          dispatch(new ErrorNotification(err.userMessages));
        }
      }
    },

  checkCodetableAccess: (adminPanelAccess: AdminInfoData, translation: CodeSelectorTranslation):
    ActionDispatching<any, CodeSelectorState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      adminPanelAccess && translation && !adminPanelAccess.codetable?.read && blockAccess({
        dispatch,
        getState,
        area: STATE.codeSelector,
        translationId: text.no_access_to_codetable_administration
      });
    }),

    loadBookmarkedCaseTypes: (): ActionDispatching<any, any> =>
        errorSafe(async (dispatch: any, getState: any) => {
          dispatch(new actions.RequestBookmarkedCodes());
          const caseTypes = await api.loadBookmarkedCaseTypes();
          dispatch(new actions.ReceiveBookmarkedCodes((caseTypes)));
    }),

    removeBookmarkCaseType: (caseTypeId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.RequestCodes());
        dispatch(new actions.RequestBookmarkedCodes());
        try {
          await api.removeBookmark(caseTypeId);
        } catch (err) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        const caseTypes = await api.caseTypes();
        dispatch(new actions.ReceiveCodes(caseTypes));
        const bookmarkedCaseTypes = await api.loadBookmarkedCaseTypes();
        dispatch(new actions.ReceiveBookmarkedCodes(bookmarkedCaseTypes));
        navigate.toSelectCaseTypes();
    }),

    bookmarkCaseType: (caseTypeId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.RequestCodes());
        dispatch(new actions.RequestBookmarkedCodes());
        try{
          await api.bookmarkCaseType(caseTypeId, {
            bookmark: true
          });
        } catch(err) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        const caseTypes = await api.caseTypes();
        dispatch(new actions.ReceiveCodes(caseTypes));
        const bookmarkedCaseTypes = await api.loadBookmarkedCaseTypes();
        dispatch(new actions.ReceiveBookmarkedCodes(bookmarkedCaseTypes));
        navigate.toSelectCaseTypes();
    }),

    changeViewModeCaseTypes: (mode: ViewMode): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        clientCache.setItem(viewModeCacheKeyCaseTypes, mode);
        dispatch(new ChangeViewModeCaseTypes(mode));
    }),

     loadDraftsCount: (): ActionDispatching<any, any> =>
      async (dispatch: any) => {
        dispatch(new RequestLoadDraftsCount());
        try {
          const draftsCount = await api.getDraftsCount();
          dispatch(new ReceiveLoadDraftsCount(draftsCount || 0));
        } catch {
          dispatch(new FailLoadDraftsCount());
        }
      }
};
